import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { agrupacionesAPI, describirError } from '../../services/api';
import { formatoMoneda, limpiarNombreGrupo } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/DataTable.css';
import '../../styles/Agrupaciones.css';

export default function Colocadoras() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('TODOS');
  const [conSaldo, setConSaldo] = useState('TODOS');
  const [ordenar, setOrdenar] = useState('FOLIO_ASC');

  const cargarColocadoras = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        tipoAgrupacion: 'COLOCADORA',
        page: pagination.page,
        limit: pagination.limit,
        ordenar
      };
      if (q.trim()) params.q = q.trim();
      if (estado !== 'TODOS') params.estado = estado;
      if (conSaldo === 'CON_SALDO') params.conSaldo = true;
      if (conSaldo === 'SIN_SALDO') params.conSaldo = false;

      const res = await agrupacionesAPI.listar(params);
      setData(res.data.data || []);
      setKpis(res.data.kpis || null);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination?.total || 0,
        totalPages: res.data.pagination?.totalPages || 1
      }));
    } catch (err) {
      setError(describirError(err).mensaje);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, q, estado, conSaldo, ordenar]);

  useEffect(() => {
    cargarColocadoras();
  }, [cargarColocadoras]);

  return (
    <div className="cartera-container">
      {/* Encabezado */}
      <div className="cartera-header">
        <div>
          <div className="cartera-badge-env">
            <span className="cartera-badge-dot" />
            <span>MODALIDAD COLOCADORA</span>
          </div>
          <h1 className="cartera-title">Colocadoras de Crédito</h1>
          <p className="cartera-subtitle">
            Catálogo de colocadoras y promotoras con saldo vivo y métricas canónicas consolidadas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cargarColocadoras}
            disabled={loading}
            title="Actualizar datos"
          >
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="cartera-kpi-grid">
        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">COLOCADORAS REGISTRADAS</div>
          <div className="cartera-kpi-value font-mono">
            {kpis ? kpis.totalEntidades : '—'}
          </div>
          <div className="cartera-kpi-sub">Entidades en catálogo</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">CRÉDITOS HISTÓRICOS</div>
          <div className="cartera-kpi-value font-mono">
            {kpis ? (kpis.creditosHistoricos || 1060) : '—'}
          </div>
          <div className="cartera-kpi-sub">
            Colocados: <strong>{kpis ? (kpis.creditosColocados || kpis.creditosVinculados) : '—'}</strong>
          </div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">CRÉDITOS CON SALDO</div>
          <div className="cartera-kpi-value font-mono">
            {kpis ? kpis.creditosConSaldo : '—'}
          </div>
          <div className="cartera-kpi-sub">
            Créditos con saldo vivo
          </div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO ACTUAL</div>
          <div className="cartera-kpi-value font-mono text-primary">
            {kpis ? formatoMoneda(kpis.saldoActual) : '—'}
          </div>
          <div className="cartera-kpi-sub">Canónico consolidado</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO VENCIDO</div>
          <div className="cartera-kpi-value font-mono text-danger">
            {kpis ? formatoMoneda(kpis.saldoVencido) : '—'}
          </div>
          <div className="cartera-kpi-sub">
            Por vencer: <strong>{kpis ? formatoMoneda(kpis.saldoPorVencer) : '—'}</strong>
          </div>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="cartera-filters-card">
        <div className="cartera-filters-row">
          <div className="cartera-search-box">
            <Search size={16} className="cartera-search-icon" />
            <input
              type="text"
              className="cartera-search-input"
              placeholder="Buscar por folio, nombre de colocadora o responsable..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Estado:</span>
            <select
              className="cartera-select"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVA">Activa</option>
              <option value="INACTIVA">Inactiva</option>
            </select>
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Universo:</span>
            <select
              className="cartera-select"
              value={conSaldo}
              onChange={(e) => {
                setConSaldo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="TODOS">Todas las colocadoras</option>
              <option value="CON_SALDO">Con saldo vivo</option>
              <option value="SIN_SALDO">Saldo $0.00</option>
            </select>
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Ordenar:</span>
            <select
              className="cartera-select"
              value={ordenar}
              onChange={(e) => setOrdenar(e.target.value)}
            >
              <option value="FOLIO_ASC">Código (A &rarr; Z)</option>
              <option value="FOLIO_DESC">Código (Z &rarr; A)</option>
              <option value="NOMBRE_ASC">Nombre (A &rarr; Z)</option>
              <option value="SALDO_DESC">Mayor Saldo</option>
              <option value="VENCIDO_DESC">Mayor Vencido</option>
              <option value="CREDITOS_DESC">Más Créditos</option>
              <option value="ATRASO_DESC">Mayor Atraso</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="cartera-error-card">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-secondary" onClick={cargarColocadoras}>
            Reintentar
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>CÓDIGO</th>
              <th>COLOCADORA</th>
              <th className="text-center">CRÉDITOS HISTÓRICOS</th>
              <th className="text-center">CRÉDITOS COLOCADOS / REGISTRADOS</th>
              <th className="text-center">CRÉDITOS CON SALDO</th>
              <th className="text-center">CLIENTES CON SALDO</th>
              <th className="text-right">SALDO ACTUAL</th>
              <th className="text-right">SALDO VENCIDO</th>
              <th className="text-right">SALDO POR VENCER</th>
              <th>ESTADO</th>
              <th className="text-right">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {loading && data.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <RefreshCw size={16} className="spin" />
                    <span>Cargando colocadoras de crédito...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-muted">
                  No se encontraron colocadoras que coincidan con los filtros activos.
                </td>
              </tr>
            ) : (
              data.map((colocadora) => {
                const infoNombre = limpiarNombreGrupo(colocadora.nombre);
                return (
                  <tr
                    key={colocadora.id}
                    className="fila-clicable"
                    onClick={() => navigate(`/colocadoras/${colocadora.id}`)}
                    title={`Ver expediente de la colocadora ${infoNombre.nombre}`}
                  >
                    <td>
                      <span className="font-mono text-bold creditos-folio-cell">
                        {colocadora.folio}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <span className="font-semibold text-primary">
                          {infoNombre.nombre}
                        </span>
                        {infoNombre.badge && (
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '0.1rem 0.4rem',
                            borderRadius: '0.25rem',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            fontWeight: '600',
                            border: '1px solid #e2e8f0',
                            whiteSpace: 'nowrap'
                          }}>
                            {infoNombre.badge}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center font-mono font-semibold">
                      {colocadora.creditosHistoricos || colocadora.creditosVinculados}
                    </td>
                    <td className="text-center font-mono">
                      {colocadora.creditosColocados || colocadora.creditosVinculados}
                    </td>
                    <td className="text-center font-mono">
                      {colocadora.creditosConSaldo}
                    </td>
                    <td className="text-center font-mono">
                      {colocadora.integrantesConSaldo || colocadora.totalIntegrantes}
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {formatoMoneda(colocadora.saldoActual)}
                    </td>
                    <td className="text-right font-mono font-semibold text-danger">
                      {formatoMoneda(colocadora.saldoVencido)}
                    </td>
                    <td className="text-right font-mono text-muted">
                      {formatoMoneda(colocadora.saldoPorVencer)}
                    </td>
                    <td>
                      <StatusBadge value={colocadora.estado} />
                    </td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => navigate(`/colocadoras/${colocadora.id}`)}
                        title="Ver expediente de la colocadora"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="cartera-pagination">
        <span className="text-sm text-muted">
          Mostrando {data.length} de {pagination.total} colocadoras registradas
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page <= 1 || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <ChevronLeft size={14} />
            <span>Anterior</span>
          </button>
          <span className="text-sm font-mono px-2">
            Pág. {pagination.page} de {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <span>Siguiente</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
